import { Loader, Table, Group, Button } from '@mantine/core';
import { useQuery, useQueryClient } from 'react-query';
import { useSelector } from 'react-redux';
import { AppState } from '../../client-redux/reducers';
import { Language } from '../../languages/languageHandler';
import { getLanguageName } from '../../languages/languageUtils';
import { fetchUsers, fetchPermissions, IUser, deleteUser, ImportCSVReport } from '../../../libraries/user/index';
import Header from '../../components/util/header';
import { LoginState } from '../../client-redux/login/reducer';
import PaginationComponent from '../../components/pagination';
import { useState, useEffect, useRef } from 'react';
import FilterBar, { FilterBarFieldsData } from '../../components/filterbar';
import { useFitTableRows } from '../../components/util/fit-table-rows-hook';
import ConfirmationModal from '../../components/confirmation-modal';
import UserModal from './user-modal';
import { UserFormMode } from './user-form';
import { fileToBase64 } from '../../utils/script';
import { useStatusOperDialog, isZarphSuccessResponse } from '../../context/status-oper-dialog';
import { preparePermissions } from './permissions';
import { IconTrash, IconFileTypeCsv } from '@tabler/icons-react';
import './styles.scss';

function Users() {
  const lang = useSelector<AppState, Language>(state => state.lang.lang);
  const login = useSelector<AppState, LoginState>(state => state.login);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const currentEntid = useSelector<AppState, number>(state => state.filters.filterBar.entity ?? 0);
  const controller = useRef(new AbortController()).current;

  const permissionOptionData = useQuery(['permissions'], fetchPermissions, {
    staleTime: Infinity,
    cacheTime: Infinity
  });
  const [loading, setLoading] = useState<boolean>(false);
  const {
    limit = 10,
    setLimit,
    tableRef
  } = useFitTableRows({
    bufferRows: 1,
    minHeightForDynamic: 400
  });
  const [offset, setOffset] = useState<number>(0);
  const [totalRows, setTotalRows] = useState<number>(0);
  const [selectedUser, setSelectedUser] = useState<IUser>();
  const [openDetails, setOpenDetails] = useState<boolean>(false);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [message, setMessage] = useState<IUser[]>([]);
  const queryClient = useQueryClient();

  const [filterBarData, setFilterBarData] = useState<FilterBarFieldsData>();
  const [showNewUserModal, setShowNewUserModal] = useState(false);
  const [deleteCandidate, setDeleteCandidate] = useState<number | null>(null);

  const { handleApiError } = useStatusOperDialog();

  useEffect(() => {
    if (filterBarData && limit) {
      loadUsersList();
    } // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterBarData, limit, offset]);

  const handleFilterBarOnDataChange = (data: FilterBarFieldsData) => {
    setFilterBarData(data);
    changePage(1);
  };

  const loadUsersList = () => {
    if (!login.token) return;

    setLoading(true);
    setMessage([]);
    setTotalRows(0);

    fetchUsers(
      controller,
      filterBarData?.entity || login.user.entid || 0,
      limit,
      offset,
      true,
      filterBarData?.name || '',
      filterBarData?.uid || 0
    )
      .then(response => {
        if (isZarphSuccessResponse(response, [9004])) {
          setMessage(response?.data?.users || []);
          setTotalRows(response?.data?.totalRows || 0);
        } else {
          handleApiError(response, lang.ALERT_ERROR_FETCHING_DATA);
        }
      })
      .catch(err => {
        handleApiError(err, lang.ALERT_ERROR_FETCHING_DATA);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const deleteActiveUser = async (entid: number, uid: number) => {
    try {
      const res = await deleteUser(entid, uid);

      if (isZarphSuccessResponse(res)) {
        queryClient.invalidateQueries('users');
        loadUsersList();
        setOpenDetails(false);
      } else {
        handleApiError(res, lang.EDIT_USER_ALERT_DELETE_ERROR);
      }
    } catch (error) {
      handleApiError(error, lang.EDIT_USER_ALERT_DELETE_ERROR);
    }
  };

  const handleCSVFileImport = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    try {
      const base64 = await fileToBase64(file);
      const res = await ImportCSVReport(
        {
          entid: currentEntid || login.user.entid,
          file: base64
        },
        controller
      );
      if (isZarphSuccessResponse(res)) {
        loadUsersList();
      } else {
        handleApiError(res, lang.USER_IMPORT_ERROR);
      }
    } catch (err) {
      handleApiError(err, lang.IMPORT_ERROR);
    } finally {
      event.target.value = '';
    }
  };

  const handleToggle =
    (index: number): (() => void) =>
    () => {
      const perms = permissionOptionData.data?.data.list || [];
      const rawUser = message[index];
      const userCopy = {
        ...rawUser,
        permissions: rawUser.permissions ?? []
      };

      preparePermissions(perms, userCopy);

      setSelectedUser(userCopy);
      setOpenDetails(true);
    };
  const isActive = (uid: number | null): boolean => {
    if (!selectedUser) return false;

    return uid === selectedUser.uid;
  };

  const closeDetailsModal = () => {
    setOpenDetails(false);
  };

  const userUpdated = () => {
    closeDetailsModal();
    loadUsersList();
  };

  const changePage = (page: number) => {
    const newOffset = (page - 1) * limit;
    setCurrentPage(page);
    setOffset(newOffset);
  };

  const resultsSelect = (newValue: number) => {
    const newLimit = Number(newValue);
    setLimit(newLimit);
    setCurrentPage(1);
    setOffset(0);
  };

  const handleAddUserToList = () => {
    setShowNewUserModal(true);
  };

  return (
    <div className="users">
      <ConfirmationModal
        opened={deleteCandidate !== null}
        onClose={() => setDeleteCandidate(null)}
        onConfirm={async () => {
          if (deleteCandidate !== null) {
            await deleteActiveUser(currentEntid, deleteCandidate);
          }
        }}
        text={lang.USER_DELETE_CONFIRM}
        successText={lang.ATYPE_USER_DELETED}
        errorText={lang.EDIT_USER_ALERT_DELETE_ERROR}
      />

      {selectedUser && lang && permissionOptionData.data && (
        <UserModal
          show={openDetails}
          mode={UserFormMode.UPDATE}
          user={selectedUser}
          lang={lang}
          permissionOptions={permissionOptionData.data.data.list}
          onClose={closeDetailsModal}
          onUserAdded={userUpdated}
          OnUserDeleted={(uid: number) => setDeleteCandidate(uid)}
        />
      )}

      {permissionOptionData.data && (
        <UserModal
          show={showNewUserModal}
          mode={UserFormMode.CREATE}
          user={{
            entid: login.user.entid,
            uid: null,
            lang: login.user.lang,
            region: login.user.region
          }}
          lang={lang}
          permissionOptions={permissionOptionData.data.data.list}
          onClose={() => setShowNewUserModal(false)}
          onUserAdded={loadUsersList}
        />
      )}

      <Header pageTitle={lang.GLOBAL_USERS} />

      <Group>
        <FilterBar
          onSearch={loadUsersList}
          onDataChange={handleFilterBarOnDataChange}
          fieldsUsage={{
            entity: true,
            name: true,
            uid: true
          }}
          addButton={handleAddUserToList}
        />
        <Button radius="md" className="file-import" onClick={() => fileInputRef.current?.click()}>
          <IconFileTypeCsv stroke={2} />
          {lang.USER_IMPORT_CSV_FILE}
        </Button>

        <input
          type="file"
          accept=".csv"
          ref={fileInputRef}
          style={{ display: 'none' }}
          onChange={handleCSVFileImport}
        />
      </Group>

      <Table.ScrollContainer minWidth={500} ref={tableRef}>
        <Table striped highlightOnHover verticalSpacing="sm">
          <Table.Thead>
            <Table.Tr>
              <Table.Th>ID</Table.Th>
              <Table.Th>{lang.GLOBAL_NAME}</Table.Th>
              <Table.Th>{lang.USER_EMAIL}</Table.Th>
              <Table.Th>{lang.USER_CARD}</Table.Th>
              <Table.Th>{lang.USER_REGION}</Table.Th>
              <Table.Th>{lang.USER_LANG}</Table.Th>
              <Table.Th></Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {loading && !message && (
              <Table.Tr>
                <Table.Td colSpan={16}>
                  <Loader color="#016273" />
                </Table.Td>
              </Table.Tr>
            )}
            {!message.length && !loading ? (
              <Table.Tr>
                <Table.Td colSpan={16} style={{ textAlign: 'center' }}>
                  {lang.GLOBAL_NO_RESULTS}
                </Table.Td>
              </Table.Tr>
            ) : (
              message?.map((user: IUser, index: number) => (
                <Table.Tr
                  key={user.uid}
                  className={`table-row ${isActive(user.uid) ? 'selectedUser' : ''}`}
                  onClick={handleToggle(index)}
                >
                  <Table.Td>{user.uid}</Table.Td>
                  <Table.Td>{user.name}</Table.Td>
                  <Table.Td>{user.email}</Table.Td>
                  <Table.Td>{user.card}</Table.Td>
                  <Table.Td>{user.region}</Table.Td>
                  <Table.Td>{getLanguageName(user.lang)}</Table.Td>
                  <Table.Td
                    onClick={e => {
                      e.stopPropagation();
                      if (!openDetails) setDeleteCandidate(user.uid);
                    }}
                  >
                    <IconTrash stroke={2} />
                  </Table.Td>
                </Table.Tr>
              ))
            )}
          </Table.Tbody>
        </Table>
      </Table.ScrollContainer>

      <Group>
        <PaginationComponent
          total={totalRows}
          itemsPerPage={limit}
          dynamicLimit={limit}
          currentPage={currentPage}
          onPageChange={changePage}
          onResultsChange={resultsSelect}
          offset={offset}
        />
      </Group>
    </div>
  );
}

export default Users;
